import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { TYPE_OPTIONS, PRIORITY_OPTIONS, toDatetimeLocal } from './calendarUtils';

/**
 * Modal de création / édition d'événement.
 * @param {object|null} event  événement à éditer (null = création)
 * @param {Function} onClose
 * @param {Function} onSubmit  reçoit le payload prêt pour l'API ; doit renvoyer une Promise
 */
export default function EventModal({ event, onClose, onSubmit }) {
  const isEdit = Boolean(event?._id);

  const [form, setForm] = useState({
    title: event?.title || '',
    type: event?.type || 'meeting',
    priority: event?.priority || 'medium',
    startDate: toDatetimeLocal(event?.startDate || Date.now()),
    endDate: toDatetimeLocal(event?.endDate || Date.now() + 60 * 60 * 1000),
    location: event?.location || '',
    description: event?.description || '',
    attendeeName: event?.attendees?.[0]?.name || '',
    attendeeEmail: event?.attendees?.[0]?.email || '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.title.trim()) return setFormError('Le titre est requis.');
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
      return setFormError('Dates invalides.');
    if (end < start) return setFormError('La date de fin doit être après la date de début.');

    const payload = {
      title: form.title.trim(),
      type: form.type,
      priority: form.priority,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      location: form.location.trim() || undefined,
      description: form.description.trim() || undefined,
      attendees: form.attendeeName.trim()
        ? [{ name: form.attendeeName.trim(), email: form.attendeeEmail.trim() }]
        : [],
    };

    try {
      setSaving(true);
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setFormError(err?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full text-ink px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-green text-sm';
  const inputStyle = { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="card w-full max-w-lg rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6" style={{ borderBottom: '0.5px solid var(--c-border)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                <CalendarIcon style={{ color: 'rgb(var(--c-accent-green-rgb))' }} size={20} />
              </div>
              <h3 className="text-lg font-semibold text-ink">
                {isEdit ? "Modifier l'événement" : 'Nouvel événement'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[var(--c-hover)] text-label hover:text-ink transition-all"
            >
              <X size={18} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
            <div>
              <label className="text-sm text-label mb-1.5 block">Titre *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                className={inputCls} style={inputStyle}
                placeholder="Ex. Démo produit - Client A"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-label mb-1.5 block">Type</label>
                <select value={form.type} onChange={(e) => set('type', e.target.value)} className={inputCls} style={inputStyle}>
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-label mb-1.5 block">Priorité</label>
                <select
                  value={form.priority}
                  onChange={(e) => set('priority', e.target.value)}
                  className={inputCls} style={inputStyle}
                >
                  {PRIORITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-label mb-1.5 block">Début *</label>
                <input
                  type="datetime-local"
                  value={form.startDate}
                  onChange={(e) => set('startDate', e.target.value)}
                  className={inputCls} style={inputStyle}
                />
              </div>
              <div>
                <label className="text-sm text-label mb-1.5 block">Fin *</label>
                <input
                  type="datetime-local"
                  value={form.endDate}
                  onChange={(e) => set('endDate', e.target.value)}
                  className={inputCls} style={inputStyle}
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-label mb-1.5 block">Lieu / Lien</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => set('location', e.target.value)}
                className={inputCls} style={inputStyle}
                placeholder="Visioconférence, Salle 2…"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-label mb-1.5 block">Participant (nom)</label>
                <input
                  type="text"
                  value={form.attendeeName}
                  onChange={(e) => set('attendeeName', e.target.value)}
                  className={inputCls} style={inputStyle}
                  placeholder="M. Dupont"
                />
              </div>
              <div>
                <label className="text-sm text-label mb-1.5 block">Participant (email)</label>
                <input
                  type="email"
                  value={form.attendeeEmail}
                  onChange={(e) => set('attendeeEmail', e.target.value)}
                  className={inputCls} style={inputStyle}
                  placeholder="dupont@ex.com"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-label mb-1.5 block">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                className={`${inputCls} h-24 resize-none`}
                placeholder="Détails de l'événement…"
              />
            </div>

            {formError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5 text-sm text-red-400">
                {formError}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 text-ink rounded-lg font-medium hover:bg-[var(--c-hover)] transition-all"
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1 py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
